import api from "lib/axios";
import type { ApiEnvelope, PaginatedResult } from "services/apiTypes";
import { unwrapData, unwrapList } from "services/apiTypes";

export type FolderItemType = "video" | "file" | "image" | "quiz";

export type FolderItem = {
  id: number;
  folder_id?: number;
  course_id?: number;
  type: FolderItemType;
  title: string;
  is_locked?: boolean;
  requires_previous?: boolean;
  sort_order?: number;
  path?: string | null;
  url?: string | null;
  meta?: Record<string, any> | null;
  quiz_id?: number | null;
  quiz?: any;
  created_at?: string;
  updated_at?: string;
};

export type FolderItemUpsertBody = {
  type: FolderItemType;
  title: string;
  is_locked?: boolean;
  requires_previous?: boolean;
  path?: string | null;
  meta?: Record<string, any> | null;
  quiz_id?: number | null;
  sort_order?: number | null;
};

export type FolderItemMoveBody = {
  folder_id: number;
  sort_order?: number | null;
};

export type FolderItemReorderRow = {
  id: number;
  sort_order: number;
  folder_id?: number;
};

export const instructorFolderItemsService = {
  async list(courseId: number | string, folderId: number | string, params: Record<string, unknown> = {}) {
    const res = await api.get<
      ApiEnvelope<PaginatedResult<FolderItem>> | ApiEnvelope<{ data: FolderItem[] }>
    >(`/instructors/courses/${courseId}/folders/${folderId}/items`, {
      params,
      meta: { toastSuccess: false },
    });
    const paginated = unwrapList<FolderItem>(res.data as any);
    if (Array.isArray(paginated.data) && (paginated.meta?.total ?? 0) >= 0) return paginated;

    const list = (res.data as any)?.result?.data;
    return {
      data: Array.isArray(list) ? list : [],
      meta: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: Array.isArray(list) ? list.length : 0,
        from: null,
        to: null,
      },
    } as PaginatedResult<FolderItem>;
  },

  async create(courseId: number | string, folderId: number | string, body: FolderItemUpsertBody) {
    const res = await api.post<ApiEnvelope<{ data: FolderItem }>>(
      `/instructors/courses/${courseId}/folders/${folderId}/items`,
      body
    );
    return unwrapData(res.data);
  },

  async update(
    courseId: number | string,
    folderId: number | string,
    itemId: number | string,
    body: FolderItemUpsertBody
  ) {
    const res = await api.put<ApiEnvelope<{ data: FolderItem }>>(
      `/instructors/courses/${courseId}/folders/${folderId}/items/${itemId}`,
      body
    );
    return unwrapData(res.data);
  },

  async remove(courseId: number | string, folderId: number | string, itemId: number | string) {
    const res = await api.delete<ApiEnvelope<unknown>>(
      `/instructors/courses/${courseId}/folders/${folderId}/items/${itemId}`
    );
    return { message: (res.data as any)?.message ?? "Deleted." };
  },

  async move(
    courseId: number | string,
    folderId: number | string,
    itemId: number | string,
    body: FolderItemMoveBody
  ) {
    const res = await api.post<ApiEnvelope<{ data: FolderItem }>>(
      `/instructors/courses/${courseId}/folders/${folderId}/items/${itemId}/move`,
      body,
      { meta: { toastSuccess: false } }
    );
    return unwrapData(res.data);
  },

  async reorder(courseId: number | string, items: FolderItemReorderRow[]) {
    await api.post<ApiEnvelope<unknown>>(
      `/instructors/courses/${courseId}/folder-items/reorder`,
      { items },
      { meta: { toast: false } }
    );
  },
};
